import { ReactNode } from 'react';
import styled from 'styled-components';

type Option = {
  value: string;
  label: ReactNode; // 문자열도 되고 <span>m<sup>2</sup></span> 같은 노드도 가능
};

/**
 * value: 현재 선택된 값
 */
interface Props {
  options: Option[];
  value: string;
  onChange: (v: string) => void;
  width?: number;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * 세그먼트 토글 버튼 (글씨 있는 토글버튼)
 * @param value 현재 선택된 값
 */
function SegmentedToggle({
  options,
  value,
  onChange,
  width,
  disabled,
  fullWidth,
  className,
  ariaLabel,
}: Props) {
  const count = options.length;
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  const handleWrapClick = (e: any) => {
    if (disabled) return;
    if (count === 2) {
      e.preventDefault();
      const next = options.find((o) => o.value !== value);
      if (next) onChange(next.value);
    }
  };

  return (
    <Wrap
      tabIndex={0}
      role="tablist"
      aria-label={ariaLabel}
      $count={count}
      $disabled={!!disabled}
      $width={width}
      onClick={(e) => {
        handleWrapClick(e);
      }}
      className={className}
    >
      <Thumb aria-hidden $index={activeIndex} $count={count} />
      {options.map((opt, i) => {
        const on = opt.value === value;
        return (
          <Item
            role="tab"
            aria-selected={on}
            key={opt.value}
            $on={on}
            onClick={(e) => {
              // e.stopPropagation();
              if (!disabled) onChange(opt.value);
            }}
          >
            {opt.label}
          </Item>
        );
      })}
    </Wrap>
  );
}

export default SegmentedToggle;

const heights = {
  sm: 26,
  md: 30,
} as const;

const paddings = {
  sm: 2,
  md: 2,
} as const;

const radius = 8;

const Wrap = styled.div<{
  $count: number;
  $disabled: boolean;
  $width?: number;
}>`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  border-radius: ${radius}px;
  padding: 2px;

  background: #0220470d; /* 연한 남색 배경 */
  overflow: hidden;
  user-select: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px #008aff33;
  }
`;

const Thumb = styled.span<{
  $index: number;
  $count: number;
}>`
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc((100% - 4px) / ${(p) => p.$count});
  height: calc(100% - 4px);
  border-radius: ${radius - 1}px;
  background: #fff;
  box-shadow: 0px 1px 2px 0px #00000014;
  transform: translateX(${(p) => `calc(${p.$index} * 100%)`});
  transition:
    transform 300ms cubic-bezier(0.2, 0.8, 0.2, 1),
    background-color 300ms ease;
  will-change: transform;
  pointer-events: none;
`;

const Item = styled.button<{ $on: boolean }>`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  border-radius: ${radius - 1}px;
  flex: 1;

  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;

  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  z-index: 1;
  color: ${({ $on }) => ($on ? '#000C1ECC' : '#0019364F')};
  transition: color 160ms ease;
`;
